/**
 * Display and de-duplication helpers for every people chooser in the app.
 *
 * Two problems, one cause. The roster is populated from two directions: the SSRS/Aderant
 * feed writes `roster/{email-slug}` (ids are `email.replace(/[^a-z0-9]/g, '_')`), while a
 * first sign-in writes `roster/{authUid}`. A person who arrives both ways therefore has TWO
 * roster documents — same human, two ids — and the feed's names are upper-case while a
 * profile built from an Auth account is title-case. As of this writing the live roster has
 * 476 documents for 473 people, and 6 of 455 active names are not upper-case.
 *
 * `personLabel` normalizes the display side so a chooser never mixes "JANE PARTNER" with
 * "Terry Champlin"; `dedupePeople` collapses the duplicate documents so the same human is
 * never offered twice.
 *
 * De-duplication here is a display safety net, NOT a data fix: the two documents still
 * exist, and picking one still writes that one's id onto the matter. The real fix is to
 * delete the stale document in Admin → Roster, which is why the roster ADMIN page must keep
 * showing duplicates -- it is the only place they can be seen and removed.
 */

/**
 * How a person's name is shown in a chooser: upper-case, since that is what the roster
 * feed supplies for all but a handful of people, and a list that mixes cases reads as two
 * different directories.
 */
export function personLabel(person) {
  if (!person) return '';
  const name = person.displayName || person.email || person.id || '';
  return String(name).toUpperCase();
}

/** How complete a roster document is — the tiebreaker when two describe one person. */
function completeness(person) {
  return (
    (person.isActive === false ? 0 : 8) +
    (person.title ? 4 : 0) +
    (person.displayName ? 2 : 0) +
    (person.employeeCode ? 1 : 0)
  );
}

/**
 * One entry per human, keyed on email (the only identifier the two document-creation paths
 * share). The more complete document wins — active over inactive, then titled over
 * untitled, so a title-filtered chooser never loses someone to a bare Auth profile — and
 * anything the winner is missing is filled in from its twin, so no information is dropped
 * with the duplicate. Ties break on the smaller id purely to keep the result stable across
 * renders. People with no email are never merged: an empty key is not a match.
 *
 * Input order is otherwise preserved (callers sort afterwards).
 */
export function dedupePeople(people) {
  const list = Array.isArray(people) ? people : [];
  const byEmail = new Map();
  const out = [];

  for (const person of list) {
    if (!person) continue;
    const key = String(person.email || '').trim().toLowerCase();
    if (!key) {
      out.push(person);
      continue;
    }
    const existing = byEmail.get(key);
    if (!existing) {
      byEmail.set(key, { person, index: out.length });
      out.push(person);
      continue;
    }

    const scoreNew = completeness(person);
    const scoreOld = completeness(existing.person);
    const winner =
      scoreNew > scoreOld ||
      (scoreNew === scoreOld && String(person.id || '') < String(existing.person.id || ''))
        ? person
        : existing.person;
    const loser = winner === person ? existing.person : person;

    // Keep the winner's identity (its id is what gets written onto a matter) but never
    // lose a field only the duplicate had — a missing title would drop the person out of
    // every title-filtered chooser.
    const merged = {
      ...loser,
      ...Object.fromEntries(Object.entries(winner).filter(([, v]) => v !== undefined && v !== '')),
      id: winner.id,
    };
    out[existing.index] = merged;
    byEmail.set(key, { person: merged, index: existing.index });
  }

  return out;
}
